/**
 * Parameter defaults and output identity of the SUV Threshold (PET)
 * operation (SR-WOR-H-27-3). The tool is a thin binding of the
 * threshold-segmentation machinery: it produces ordinary
 * {@link BodyContourParams} whose `thresholdHu` field carries the RAW
 * activity threshold resolved from the %-of-SUVmax slider (see
 * {@link suvThresholdRawFromPct}) — the pipeline, mask cache and preview
 * keys are reused unchanged, the raw threshold being a pure function of
 * (percent, snapped window) so the keys stay exactly as stable as a
 * percent key would be.
 */
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { Image3D } from '~/domain/patient/image3d';
import { SuvQuantification } from '~/domain/patient/petSuv';
import { VoiShape } from '~/domain/services/imageService/gridGeometry';
import { VoiBounds } from '~/state/atoms/toolsAtoms';
import {
    BodyContourParams,
    ThresholdToolEligibility,
} from '../bodyContour/bodyContourParams';

/** Preview-key preset token: distinguishes this tool's previews from the
 *  CT presets sharing the key builders (never a CT tab id). */
export const SUV_THRESHOLD_PRESET_ID = 'suvThreshold';

export const SUV_THRESHOLD_DEFAULT_PCT = 40;
/**
 * 1 %, not 0. At 0 the raw threshold is 0 and `voxel >= 0` accepts every
 * voxel of the box including air, so Apply would write the whole region
 * as one structure with an SUVmean averaging background — a clinically
 * meaningless structure the tool has no way to distinguish from an
 * intended one. Every slider position stays meaningful instead ("no dead
 * travel", as `resolveThresholdRange` puts it for the CT presets).
 */
export const SUV_THRESHOLD_MIN_PCT = 1;
export const SUV_THRESHOLD_MAX_PCT = 100;

/**
 * Opening extent of the mandatory region box, mm per axis (5 cm cube,
 * clamped to the image). Lesion-scaled, NOT the CT tool's
 * half-the-image seed: on a whole-body PET half the extent is most of
 * the torso, whose SUVmax (brain, heart, bladder) is practically never
 * the lesion the user came to segment — the box would open quoting a
 * wrong maximum. 5 cm comfortably frames typical FDG-avid lesions
 * (PERCIST's SUVpeak sphere is 1.2 cm across) while staying visibly a
 * region on all three planes.
 */
export const SUV_THRESHOLD_DEFAULT_BOX_MM = 50;

/**
 * Opening shape of the mandatory region: the ellipsoid inscribed in the
 * box, which at the cubic default extent is a 5 cm sphere — the VOI the
 * clinical request names, and the shape a lesion actually has. A box
 * reaches √3 ≈ 1.73× further at its corners than the sphere it contains,
 * and on a whole-body PET that corner is exactly where a neighbouring
 * node or the bladder edge slips in and takes over the SUVmax the whole
 * threshold is relative to. The box remains available for a lesion the
 * ellipsoid cannot frame.
 */
export const SUV_THRESHOLD_DEFAULT_SHAPE: VoiShape = 'ellipsoid';

/**
 * Default floor for the optional min-size stage, as per-slice in-plane
 * area (mm², domain unit — displayed as cm²). PET blobs are small: a
 * single 4 mm voxel is 16 mm², so 20 mm² ≈ "more than one voxel per
 * slice" — the CT default (500 mm²) would eat real lesions.
 */
export const SUV_THRESHOLD_MIN_PART_AREA_MM2 = 20;
export const SUV_THRESHOLD_MIN_PART_AREA_FLOOR_MM2 = 10;

/** SUVbw eligibility gate + refusal message for the shared preview/apply
 *  hooks — the PET analogue of the hooks' default HU gate. */
export const SUV_ELIGIBILITY: ThresholdToolEligibility = {
    isQuantified: (image: Image3D) => image.isSuvQuantified,
    refusalMessageId: 'suvThreshold.notSuvPet',
};

/**
 * Output identity: a plainly named structure carrying no structure code,
 * in the house default-structure dark blue, with GTV as the interpreted
 * type OFFERED for a structure the operation creates.
 *
 * What the operation produces is the voxels above a threshold. Whether
 * they are a gross tumour volume is a clinical reading of the image and
 * the user's to make, so the type is a control they see and can change
 * before applying — never a stamp behind the result. GTV is what that
 * control offers because thresholded PET uptake is delineated as one in
 * the overwhelming majority of cases. The NAME deliberately does not
 * repeat the claim: a result left at the default still reads as
 * "produced by SUV thresholding" rather than as an asserted target, and
 * an existing structure chosen as the target keeps the type it has.
 */
export const SUV_THRESHOLD_OUTPUT = {
    name: 'SUVThreshold',
    defaultInterpretedType: RoiInterpretedType.GTV,
    color: { R: 40, G: 90, B: 180 },
} as const;

/** The user-adjustable stage values of the SUV panel. */
export interface SuvThresholdStageParams {
    fillHolesEnabled: boolean;
    smoothingEnabled: boolean;
    smoothingStrength: number;
    minPartSizeEnabled: boolean;
    minPartAreaMm2: number;
    keepLargest3dEnabled: boolean;
    keepLargest3dCount: number;
}

/**
 * Map the SUV panel's state onto the shared params shape. Stages the SUV
 * tool does not offer are locked off: keep-largest 2D (no PET use case —
 * in-plane satellites above a box-local threshold are usually real
 * uptake), final re-fill (mirror of the fill decision), margin (belongs
 * to the margin tool), keep-inside-EXTERNAL (a box-local 40 %-of-max
 * threshold cannot reach couch/cable artifacts the way a global CT
 * threshold can, and a PET-primary structure set rarely carries an
 * EXTERNAL — a deliberate v1 omission, not an oversight).
 *
 * @param rawThreshold Inclusive lower bound in RAW image units
 *   ({@link suvThresholdRawFromPct}).
 * @param boundsMm The mandatory region box (the threshold is defined
 *   relative to its content — never null by the time params are built).
 * @param regionShape Body the region occupies within `boundsMm`.
 */
export function buildSuvThresholdParams(
    rawThreshold: number,
    stages: SuvThresholdStageParams,
    boundsMm: VoiBounds,
    regionShape: VoiShape
): BodyContourParams {
    return {
        presetId: SUV_THRESHOLD_PRESET_ID,
        thresholdHu: rawThreshold,
        fillHoles2dEnabled: stages.fillHolesEnabled,
        smoothingEnabled: stages.smoothingEnabled,
        smoothingStrength: stages.smoothingStrength,
        minPartSizeEnabled: stages.minPartSizeEnabled,
        minPartAreaMm2: stages.minPartAreaMm2,
        keepLargest2dEnabled: false,
        keepLargest2dCount: 1,
        keepLargest3dEnabled: stages.keepLargest3dEnabled,
        keepLargest3dCount: stages.keepLargest3dCount,
        finalFillHoles2dEnabled: false,
        marginMm: 0,
        keepInsideExternal: null,
        boundsMm,
        regionShape,
    };
}

/**
 * Generation description carrying the relative setting, the resolved
 * absolute value, AND the conversion provenance — for a computed factor
 * the factor itself (`SUV = raw × f`, the one number that keeps the
 * record reproducible after the source PET's SUV tags are stripped, e.g.
 * by the anonymizer), for a scanner pre-converted series the marker that
 * no conversion was applied. Weight/dose/times cannot fit: the target is
 * `ROIGenerationDescription` (3006,0038), VR LO — 64 chars — and the
 * setter truncates, so this string is built to fit rather than rely on
 * truncation. The full inputs remain derivable from the source DICOM.
 */
export function describeSuvGeneration(
    thresholdPct: number,
    absoluteSuv: number,
    quantification: SuvQuantification | null,
    regionShape: VoiShape
): string {
    const provenance =
        quantification?.state === 'preconverted-gml'
            ? '; scanner GML'
            : quantification?.factor != null
              ? `; f=${quantification.factor.toPrecision(4)}`
              : '';
    // The region shape names WHICH maximum the percentage is relative
    // to, so it belongs beside the percentage rather than in the
    // parenthetical. "SUVbw" is dropped from the absolute value to pay
    // for it: the unit (g/ml) is still explicit, and "SUV"/"SUVmax"
    // already name the quantity twice.
    return (
        `SUV thr ${thresholdPct}% of ${regionShape} SUVmax ` +
        `(${absoluteSuv.toFixed(1)} g/ml${provenance})`
    );
}
